import type { Scene } from '@babylonjs/core/scene';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { RenderingConfig } from '../config/rendering.config';
import { DEG_TO_RAD } from '../utils/math';

/**
 * Own light for the runner (user 2026-10-03: "man sieht die Figur schwer am Handy … mehr Kontrast mit der Figur"). The
 * low evening sun stands ahead of the runner, so the camera always sees the shadow side – dark workwear on dark asphalt.
 * A key light from behind the camera, above and a little to the side brightens the side the player sees. It lights only
 * the runner's meshes (includedOnlyMeshes): the scene keeps its look, the runner stands out. Strength per quality profile
 * (phones the most), dimmed with the sun at night and in enclosures.
 *
 * The light is created once the runner has meshes – an enabled light with an empty include list would light every mesh.
 */
export class CharacterLight {
  private light: DirectionalLight | null = null;
  private root: TransformNode | null = null;
  private readonly direction = new Vector3();

  constructor(
    private readonly scene: Scene,
    private readonly config: RenderingConfig['characterLight'],
    /** Quality profile scale (QualityProfile.characterLight) – 0 switches the light off. */
    private readonly scale: number,
  ) {}

  /**
   * Follows the runner's visual (`root`, re-synced when it changed – character switch, stand-in replaced), the track
   * heading `yaw` and the share of the sun left (`daylight`, 1 = open day).
   */
  update(root: TransformNode, yaw: number, daylight: number): void {
    if (this.scale <= 0) return;
    if (root !== this.root) this.attach(root);
    const light = this.light;
    if (!light) return;
    // A disposed mesh leaves every include list (Babylon) – an empty list would light the whole scene.
    const lit = light.includedOnlyMeshes.length > 0;
    if (light.isEnabled() !== lit) light.setEnabled(lit);
    if (!lit) return;
    const { elevationDegrees, sideDegrees, minDaylight } = this.config;
    // Light travels forward (from behind the camera) and down, turned a little to the side.
    const heading = yaw + sideDegrees * DEG_TO_RAD;
    const elevation = elevationDegrees * DEG_TO_RAD;
    this.direction.set(Math.sin(heading) * Math.cos(elevation), -Math.sin(elevation), Math.cos(heading) * Math.cos(elevation));
    light.direction.copyFrom(this.direction);
    const share = minDaylight + (1 - minDaylight) * Math.min(1, Math.max(0, daylight));
    light.intensity = this.config.intensity * this.scale * share;
  }

  dispose(): void {
    this.light?.dispose();
    this.light = null;
    this.root = null;
  }

  private attach(root: TransformNode): void {
    const meshes = root.getChildMeshes(false);
    if (meshes.length === 0) {
      // no meshes yet: never light the whole scene – off until the next frame finds some
      this.light?.setEnabled(false);
      return;
    }
    this.root = root;
    if (!this.light) {
      this.light = new DirectionalLight('LIGHT_CHARACTER', new Vector3(0, -1, 1), this.scene);
      this.light.diffuse = Color3.FromHexString(this.config.color);
      this.light.specular = this.light.diffuse.scale(this.config.specular);
      this.light.shadowEnabled = false;
    }
    // Sun, runner lamp and this one (the ambient light is off with the environment) stay within Babylon's 4 lights per
    // material – no material needs more.
    this.light.includedOnlyMeshes = meshes;
  }
}
